"use client"
import { useState, useEffect, useCallback } from "react" // Added useEffect and useCallback
import { Link } from "react-router-dom"
import { fetchPricingSettings } from "../utils/auth" // Import the new pricing fetch function

const Subscription = () => {
  const [billingCycle, setBillingCycle] = useState("monthly")
  const [pricingPlans, setPricingPlans] = useState([]) // State to store fetched pricing plans
  const [loading, setLoading] = useState(true) // Loading state for API call
  const [error, setError] = useState(null) // Error state for API call

  // Fetch pricing settings on component mount
  useEffect(() => {
    const getPricing = async () => {
      try {
        setLoading(true)
        const data = await fetchPricingSettings()
        // Ensure that the fetched data matches the expected structure for 'plans'
        // Transform the data if needed, assuming the backend returns an array of plan objects
        setPricingPlans(data || []) // API returns plans array directly
        setError(null)
      } catch (err) {
        console.error("Failed to fetch pricing settings:", err)
        setError("Failed to load pricing plans. Please try again later.")
      } finally {
        setLoading(false)
      }
    }
    getPricing()
  }, []) // Empty dependency array means this runs once on mount

  // Update button links based on billing cycle (useCallback for memoization)
  const getButtonLink = useCallback(
    (plan) => {
      if (plan.name === "Free") {
        return "/home"
      }

      // Ensure plan.price[billingCycle] exists before accessing
      const amount = plan.price?.[billingCycle] ?? 0 // Use nullish coalescing for safety
      const planType = plan.name.toLowerCase()
      return `/payments?plan=${planType}&amount=${amount}&billing=${billingCycle}`
    },
    [billingCycle], // Recalculate if billingCycle changes
  )

  if (loading) {
    return (
      <div className="subscription-page" style={{ textAlign: "center", padding: "5rem 0" }}>
        Loading pricing plans...
      </div>
    )
  }

  if (error) {
    return (
      <div className="subscription-page" style={{ textAlign: "center", padding: "5rem 0", color: "red" }}>
        {error}
      </div>
    )
  }

  // Use pricingPlans from state
  const plansToDisplay =
    pricingPlans.length > 0
      ? pricingPlans
      : [
          // Fallback or default plans if no data is fetched
          {
            name: "Free",
            price: { monthly: 0, yearly: 0 },
            description: "Perfect for testing and small projects",
            features: [
              "Export up to 50 products per day",
              "Basic product information",
              "CSV export format",
              "Single store scraping",
              "Email support",
            ],
            buttonText: "Start Free",
            buttonLink: "/home",
            highlighted: false,
          },
          {
            name: "Professional",
            price: { monthly: 14.99, yearly: 140 },
            description: "Ideal for agencies and growing businesses",
            features: [
              "Export unlimited products",
              "Complete product data (images, variants, descriptions)",
              "Multiple export formats (CSV, JSON, Excel)",
              "Collection and category filtering",
              "Bulk store scraping",
              "Advanced search and filtering",
              "Priority email support",
              "API access",
            ],
            buttonText: "Go Professional",
            buttonLink: "/payments?plan=professional&amount=14.99&billing=monthly",
            highlighted: true,
          },
          {
            name: "Business", // Corrected typo from "Bussiness"
            price: { monthly: 29.99, yearly: 300 },
            description: "Complete solution for large-scale operations",
            features: [
              "Everything in Professional plan",
              "Unlimited store scraping",
              "Custom data field extraction",
              "White-label exports",
              "Multiple user accounts",
              "Advanced API with webhooks",
              "Custom integrations",
              "Dedicated account manager",
              "24/7 phone support",
            ],
            buttonText: "Get Business", // Corrected typo from "Bussiness"
            buttonLink: "/payments?plan=business&amount=29.99&billing=monthly",
            highlighted: false,
          },
        ]

  return (
    <div className="subscription-page">
      {/* Hero Section */}
      <section className="subscription-hero">
        <div className="subscription-hero-container">
          <h1 className="subscription-hero-title">Shopify Scraper Pricing</h1>
          <p className="subscription-hero-description">
            Extract product data from any Shopify store with ease. Choose the plan that scales with your business needs.
          </p>

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <span className={`billing-option ${billingCycle === "monthly" ? "active" : ""}`}>Monthly</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={billingCycle === "yearly"}
                onChange={() => setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly")}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className={`billing-option ${billingCycle === "yearly" ? "active" : ""}`}>
              Yearly <span className="save-badge">Save 17%</span>
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="subscription-plans-section">
        <div className="subscription-plans-container">
          <div className="subscription-plans">
            {plansToDisplay.map(
              (
                plan,
                index, // Use plansToDisplay here
              ) => (
                <div className={`subscription-plan ${plan.highlighted ? "highlighted" : ""}`} key={index}>
                  <div className="plan-header">
                    <h3 className="plan-name">{plan.name}</h3>
                    <div className="plan-price">
                      <span className="price-amount">${plan.price[billingCycle]?.toFixed(2) ?? "0.00"}</span>{" "}
                      {/* Safely access price and format */}
                      <span className="price-cycle">{billingCycle === "monthly" ? "/month" : "/year"}</span>
                    </div>
                    <p className="plan-description">{plan.description}</p>
                  </div>
                  <div className="plan-features">
                    <ul>
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex}>
                          <span className="feature-check">✓</span> {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="plan-footer">
                    <Link to={getButtonLink(plan)} className="plan-button">
                      {plan.buttonText}
                    </Link>
                  </div>
                  {plan.highlighted && <div className="popular-badge">Most Popular</div>}
                </div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="features-comparison-section">
        <div className="features-comparison-container">
          <h2 className="features-comparison-title">Feature Comparison</h2>
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="feature-column">Features</div>
              <div className="plan-column">Free</div>
              <div className="plan-column">Professional</div>
              <div className="plan-column">Business</div> {/* Corrected typo */}
            </div>
            {/* Dynamically render comparison rows based on fetched plans and their features */}
            {plansToDisplay.length > 0 && (
              <>
                <div className="comparison-row">
                  <div className="feature-name">Products per day</div>
                  <div className="feature-value">
                    {plansToDisplay
                      .find((p) => p.name === "Free")
                      ?.features.includes("Export up to 50 products per day")
                      ? "50"
                      : "✗"}
                  </div>
                  <div className="feature-value">
                    {plansToDisplay
                      .find((p) => p.name === "Professional")
                      ?.features.includes("Export unlimited products")
                      ? "Unlimited"
                      : "✗"}
                  </div>
                  <div className="feature-value">
                    {plansToDisplay.find((p) => p.name === "Business")?.features.includes("Export unlimited products")
                      ? "Unlimited"
                      : "✗"}
                  </div>
                </div>
                <div className="comparison-row">
                  <div className="feature-name">Export formats</div>
                  <div className="feature-value">
                    {plansToDisplay.find((p) => p.name === "Free")?.features.includes("CSV export format")
                      ? "CSV"
                      : "✗"}
                  </div>
                  <div className="feature-value">
                    {plansToDisplay
                      .find((p) => p.name === "Professional")
                      ?.features.includes("Multiple export formats (CSV, JSON, Excel)")
                      ? "CSV, JSON, Excel"
                      : "✗"}
                  </div>
                  <div className="feature-value">
                    {plansToDisplay
                      .find((p) => p.name === "Business")
                      ?.features.includes("Multiple export formats (CSV, JSON, Excel)")
                      ? "CSV, JSON, Excel, Custom"
                      : "✗"}
                  </div>{" "}
                  {/* Assuming Custom is a business feature */}
                </div>
                <div className="comparison-row">
                  <div className="feature-name">API access</div>
                  <div className="feature-value">
                    {plansToDisplay.find((p) => p.name === "Free")?.features.includes("API access") ? "✓" : "✗"}
                  </div>
                  <div className="feature-value">
                    {plansToDisplay.find((p) => p.name === "Professional")?.features.includes("API access") ? "✓" : "✗"}
                  </div>
                  <div className="feature-value">
                    {plansToDisplay.find((p) => p.name === "Business")?.features.includes("Advanced API with webhooks")
                      ? "✓ Advanced"
                      : "✗"}
                  </div>
                </div>
                <div className="comparison-row">
                  <div className="feature-name">Support</div>
                  <div className="feature-value">
                    {plansToDisplay.find((p) => p.name === "Free")?.features.includes("Email support") ? "Email" : "✗"}
                  </div>
                  <div className="feature-value">
                    {plansToDisplay.find((p) => p.name === "Professional")?.features.includes("Priority email support")
                      ? "Priority Email"
                      : "✗"}
                  </div>
                  <div className="feature-value">
                    {plansToDisplay.find((p) => p.name === "Business")?.features.includes("24/7 phone support")
                      ? "24/7 Phone + Email"
                      : "✗"}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Styling */}
      <style jsx="true">{`
        /* Subscription Hero Section */
        .subscription-hero {
          background-color: #007BFF;
          color: white;
          padding: 5rem 0;
          text-align: center;
        }
        
        .subscription-hero-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .subscription-hero-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }
        
        .subscription-hero-description {
          font-size: 1.25rem;
          max-width: 800px;
          margin: 0 auto 2rem;
          opacity: 0.9;
        }
        
        /* Billing Toggle */
        .billing-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 2rem;
        }
        
        .billing-option {
          font-size: 1rem;
          opacity: 0.7;
          transition: opacity 0.2s;
          display: flex;
          align-items: center;
        }
        
        .billing-option.active {
          opacity: 1;
          font-weight: 600;
        }
        
        .save-badge {
          background-color: #f33a3a;
          color: white;
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 1rem;
          margin-left: 0.5rem;
        }
        
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 60px;
          height: 30px;
        }
        
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(255, 255, 255, 0.3);
          transition: 0.4s;
          border-radius: 34px;
        }
        
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 22px;
          width: 22px;
          left: 4px;
          bottom: 4px;
          background-color: white;
          transition: 0.4s;
          border-radius: 50%;
        }
        
        input:checked + .toggle-slider {
          background-color: rgba(255, 255, 255, 0.3);
        }
        
        input:checked + .toggle-slider:before {
          transform: translateX(30px);
        }
        
        /* Subscription Plans */
        .subscription-plans-section {
          padding: 5rem 0;
          background-color: #f9fafb;
        }
        
        .subscription-plans-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .subscription-plans {
          display: flex;
          justify-content: center;
          gap: 2rem;
          margin-top: -3rem;
        }
        
        .subscription-plan {
          background-color: white;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          padding: 2rem;
          width: 100%;
          max-width: 350px;
          flex: 1;
          display: flex;
          flex-direction: column;
          position: relative;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .subscription-plan:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
        }
        
        .subscription-plan.highlighted {
          border: 2px solid #007BFF;
          transform: scale(1.05);
        }
        
        .subscription-plan.highlighted:hover {
          transform: scale(1.05) translateY(-5px);
        }
        
        .plan-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        
        .plan-name {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #1a202c;
        }
        
        .plan-price {
          margin-bottom: 1rem;
        }
        
        .price-amount {
          font-size: 2.5rem;
          font-weight: 700;
          color: #007BFF;
        }
        
        .price-cycle {
          font-size: 1rem;
          color: #4a5568;
        }
        
        .plan-description {
          font-size: 1rem;
          color: #4a5568;
        }
        
        .plan-features {
          flex: 1;
          margin-bottom: 2rem;
        }
        
        .plan-features ul {
          list-style: none;
          padding: 0;
        }
        
        .plan-features li {
          margin-bottom: 0.75rem;
          color: #4a5568;
          display: flex;
          align-items: flex-start;
        }
        
        .feature-check {
          color: #007BFF;
          margin-right: 0.5rem;
          font-weight: bold;
        }
        
        .plan-footer {
          text-align: center;
        }
        
        .plan-button {
          display: inline-block;
          width: 100%;
          padding: 0.75rem 1.5rem;
          background-color: #007BFF;
          color: white;
          border-radius: 0.375rem;
          font-weight: 600;
          text-decoration: none;
          transition: background-color 0.2s;
        }
        
        .plan-button:hover {
          background-color: #008f85;
        }
        
        .popular-badge {
          position: absolute;
          top: -12px;
          right: 20px;
          background-color: #f33a3a;
          color: white;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
        }
        
        /* Features Comparison */
        .features-comparison-section {
          padding: 5rem 0;
          background-color: white;
        }
        
        .features-comparison-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .features-comparison-title {
          font-size: 2.25rem;
          font-weight: 700;
          margin-bottom: 3rem;
          text-align: center;
          color: #1a202c;
        }
        
        .comparison-table {
          background-color: #f9fafb;
          border-radius: 0.5rem;
          overflow: hidden;
        }
        
        .comparison-header {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          background-color: #007BFF;
          color: white;
          font-weight: 600;
        }
        
        .comparison-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .comparison-row:last-child {
          border-bottom: none;
        }
        
        .feature-column,
        .plan-column,
        .feature-name,
        .feature-value {
          padding: 1rem;
          text-align: center;
        }
        
        .feature-column,
        .feature-name {
          text-align: left;
          font-weight: 600;
        }
        
        .feature-value {
          color: #4a5568;
        }
        
        /* Responsive Styles */
        @media (max-width: 1024px) {
          .subscription-plans {
            flex-wrap: wrap;
          }
          
          .subscription-plan {
            max-width: calc(50% - 1rem);
            flex: none;
          }
          
          .comparison-header,
          .comparison-row {
            grid-template-columns: 1fr;
          }
          
          .plan-column {
            display: none;
          }
          
          .feature-value {
            display: none;
          }
        }
        
        @media (max-width: 768px) {
          .subscription-hero-title {
            font-size: 2rem;
          }
          
          .subscription-hero-description {
            font-size: 1.125rem;
          }
          
          .subscription-plans {
            flex-direction: column;
            align-items: center;
          }
          
          .subscription-plan {
            max-width: 100%;
            width: 100%;
          }
          
          .subscription-plan.highlighted {
            transform: none;
          }
          
          .subscription-plan.highlighted:hover {
            transform: translateY(-5px);
          }
          
          .features-comparison-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
    </div>
  )
}

export default Subscription
